import type { paths } from '@/types/generated/openapi';

// Union of all OpenAPI path strings, e.g. '/api/v1/organization'.
type OpenApiPath = keyof paths;

// Remove the '/api' prefix so runtime URLs match the axios baseURL setup
// (baseURL is '/api' in dev and `${VITE_API_BASE_URL}` — which ends in '/api' — in prod).
type StripApiPrefix<T extends string> = T extends `/api${infer Rest}` ? Rest : T;

// Path templates that contain at least one '{param}' placeholder.
type PathWithParam = Extract<OpenApiPath, `${string}{${string}}${string}`>;

// Replace every '{param}' placeholder with a concrete string segment in the return type.
type ReplaceParams<T extends string> = T extends `${infer A}{${string}}${infer B}`
  ? `${A}${string}${ReplaceParams<B>}`
  : T;

const encodeSegment = (value: string): string => encodeURIComponent(value);

// Runtime helper that strips '/api' while keeping the return type aligned.
const stripApiPrefix = <T extends OpenApiPath>(path: T): StripApiPrefix<T> =>
  (path.startsWith('/api/') ? path.slice(4) : path) as StripApiPrefix<T>;

// Inject one or more '{param}' values into a template (URL-encoded) and strip '/api'.
const withParams = <T extends PathWithParam>(
  template: T,
  params: Record<string, string>,
): ReplaceParams<StripApiPrefix<T>> => {
  let result: string = stripApiPrefix(template);
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(`{${key}}`, encodeSegment(value));
  }
  return result as ReplaceParams<StripApiPrefix<T>>;
};

// Single-param convenience wrapper (mirrors EHR's `withId`).
const withId = <T extends PathWithParam>(
  template: T,
  params: { id: string; paramName: string },
): ReplaceParams<StripApiPrefix<T>> => withParams(template, { [params.paramName]: params.id });

// Entity-scoped paths, derived from the generated spec so every literal below is
// compile-checked against `keyof paths`. Static routes use `stripApiPrefix`;
// parameterized routes are functions that inject (and encode) their params.
const apiPaths = {
  superAdmin: {
    searchOrganizations: stripApiPrefix('/api/v1/super-admin/search-organizations'),
    selectOrganization: stripApiPrefix('/api/v1/super-admin/select-organization'),
    currentContext: stripApiPrefix('/api/v1/super-admin/current-context'),
    clearOrganization: stripApiPrefix('/api/v1/super-admin/clear-organization'),
    manualQueue: (queue: string) =>
      withParams('/api/v1/admin/manual-queues/{queue}', { queue }),
    manualQueueAction: (queue: string, id: string) =>
      withParams('/api/v1/admin/manual-queues/{queue}/items/{id}/actions', { queue, id }),
  },

  featureFlags: {
    // Super-admin read + update of product feature flags.
    admin: stripApiPrefix('/api/v1/admin/feature-flags'),
  },

  // Monitoring metrics (identity + video pipeline snapshots, alerts, health).
  metrics: {
    identity: stripApiPrefix('/api/v1/metrics/identity'),
    identitySnapshot: stripApiPrefix('/api/v1/metrics/identity/snapshot'),
    identityHealth: stripApiPrefix('/api/v1/metrics/identity/health'),
    videoPipeline: stripApiPrefix('/api/v1/metrics/video-pipeline'),
    videoPipelineSnapshot: stripApiPrefix('/api/v1/metrics/video-pipeline/snapshot'),
    featureFlags: stripApiPrefix('/api/v1/metrics/feature-flags'),
    alerts: stripApiPrefix('/api/v1/metrics/alerts'),
    alertsSummary: stripApiPrefix('/api/v1/metrics/alerts/summary'),
    health: stripApiPrefix('/api/v1/metrics/health'),
  },

  assessments: {
    root: stripApiPrefix('/api/v1/assessments/assessment'),
    create: stripApiPrefix('/api/v1/assessments/assessment/create'),
    byId: (id: string) => withId('/api/v1/assessments/assessment/{id}', { id, paramName: 'id' }),
    userDocuments: (id: string) => withId('/api/v1/assessments/users/{id}/documents', { id, paramName: 'id' }),
    userScreeners: (id: string) => withId('/api/v1/assessments/users/{id}/screeners', { id, paramName: 'id' }),
    screenerById: (id: string) => withId('/api/v1/assessments/screeners/{id}', { id, paramName: 'id' }),
  },

  contracts: {
    root: stripApiPrefix('/api/v1/contracts'),
    byId: (contractId: string) => withId('/api/v1/contracts/{contractId}', { id: contractId, paramName: 'contractId' }),
    used: (contractId: string) =>
      withId('/api/v1/contracts/{contractId}/used', { id: contractId, paramName: 'contractId' }),
    total: (contractId: string) =>
      withId('/api/v1/contracts/{contractId}/total', { id: contractId, paramName: 'contractId' }),
    softDelete: (contractId: string) =>
      withId('/api/v1/contracts/{contractId}/soft-delete', { id: contractId, paramName: 'contractId' }),
    documents: (contractId: string) =>
      withId('/api/v1/contracts/{contractId}/documents', { id: contractId, paramName: 'contractId' }),
    documentByKey: (contractId: string, key: string) =>
      withParams('/api/v1/contracts/{contractId}/documents/{key}', { contractId, key }),
    organizationContracts: (organizationId: string) =>
      withId('/api/v1/contracts/organization/{organizationId}', { id: organizationId, paramName: 'organizationId' }),
    organizationActiveContract: (organizationId: string) =>
      withId('/api/v1/contracts/organization/{organizationId}/active', {
        id: organizationId,
        paramName: 'organizationId',
      }),
  },

  legal: {
    root: stripApiPrefix('/api/v1/legal-documents'),
    byId: (id: string) => withId('/api/v1/legal-documents/{id}', { id, paramName: 'id' }),
    byType: (type: string) => withId('/api/v1/legal-documents/type/{type}', { id: type, paramName: 'type' }),
    activate: (type: string, version: string) =>
      withParams('/api/v1/legal-documents/activate/{type}/{version}', { type, version }),
    deactivate: (type: string, version: string) =>
      withParams('/api/v1/legal-documents/deactivate/{type}/{version}', { type, version }),
  },

  memberCare: {
    ownProfile: stripApiPrefix('/api/v1/membercare/me'),
    users: stripApiPrefix('/api/v1/membercare/users'),
    updateProfile: stripApiPrefix('/api/v1/membercare/profile'),
    invite: stripApiPrefix('/api/v1/membercare/invite'),
    members: stripApiPrefix('/api/v1/membercare/my-members/scores'),
    myOrganizations: stripApiPrefix('/api/v1/membercare/my-organizations'),
    userById: (id: string) => withId('/api/v1/membercare/users/{id}', { id, paramName: 'id' }),
    activateUser: (id: string) => withId('/api/v1/membercare/users/{id}/activate', { id, paramName: 'id' }),
    deactivateUser: (id: string) => withId('/api/v1/membercare/users/{id}/deactivate', { id, paramName: 'id' }),
    assignOrganizations: (id: string) =>
      withId('/api/v1/membercare/users/{id}/assign-organizations', { id, paramName: 'id' }),
    unassignOrganizations: (id: string) =>
      withId('/api/v1/membercare/users/{id}/unassign-organizations', { id, paramName: 'id' }),
    userDetails: (id: string) => withId('/api/v1/membercare/users/{id}/details', { id, paramName: 'id' }),
    unassignedOrganizations: (id: string) =>
      withId('/api/v1/membercare/users/member-care/{id}/unassigned-organizations', { id, paramName: 'id' }),
    organizationRoster: (organizationId: string) =>
      withId('/api/v1/membercare/my-organizations/{organizationId}/roster', {
        id: organizationId,
        paramName: 'organizationId',
      }),
    organizationDashboard: (organizationId: string) =>
      withId('/api/v1/membercare/organizations/{organizationId}/dashboard', {
        id: organizationId,
        paramName: 'organizationId',
      }),
    organizationMemberCares: (organizationId: string) =>
      withId('/api/v1/membercare/organization/{organization_id}/member-cares', {
        id: organizationId,
        paramName: 'organization_id',
      }),
  },

  meta: {
    usStates: stripApiPrefix('/api/v1/meta/us-states'),
  },

  notes: {
    root: stripApiPrefix('/api/v1/notes'),
    byId: (id: string) => withId('/api/v1/notes/{id}', { id, paramName: 'id' }),
    forMember: (userId: string) => withId('/api/v1/notes/user/{userId}', { id: userId, paramName: 'userId' }),
  },

  notifications: {
    root: stripApiPrefix('/api/v1/notifications'),
    unreadCount: stripApiPrefix('/api/v1/notifications/count'),
    markAllRead: stripApiPrefix('/api/v1/notifications/mark-all-read'),
    markMultipleRead: stripApiPrefix('/api/v1/notifications/mark-multiple-read'),
    adminBulk: stripApiPrefix('/api/v1/notifications/admin/bulk'),
    adminAll: stripApiPrefix('/api/v1/notifications/admin/all'),
    byId: (id: string) => withId('/api/v1/notifications/{id}', { id, paramName: 'id' }),
    readStatus: (id: string) => withId('/api/v1/notifications/{id}/read-status', { id, paramName: 'id' }),
    adminById: (id: string) => withId('/api/v1/notifications/admin/notifications/{id}', { id, paramName: 'id' }),
  },

  observations: {
    root: stripApiPrefix('/api/v1/observations'),
    byId: (id: string) => withId('/api/v1/observations/{id}', { id, paramName: 'id' }),
    forMember: (userId: string) => withId('/api/v1/observations/user/{userId}', { id: userId, paramName: 'userId' }),
  },

  organization: {
    root: stripApiPrefix('/api/v1/organization'),
    inviteUsers: stripApiPrefix('/api/v1/organization/invite/users'),
    byId: (id: string) => withId('/api/v1/organization/{id}', { id, paramName: 'id' }),
    users: (id: string) => withId('/api/v1/organization/{id}/users', { id, paramName: 'id' }),
    inviteAdmin: (id: string) => withId('/api/v1/organization/{id}/invite-admin', { id, paramName: 'id' }),
    uploadLogo: (id: string) => withId('/api/v1/organization/{id}/upload-logo', { id, paramName: 'id' }),
    deactivate: (id: string) => withId('/api/v1/organization/{id}/deactivate', { id, paramName: 'id' }),
    reactivate: (id: string) => withId('/api/v1/organization/{id}/reactivate', { id, paramName: 'id' }),
    reinvite: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/reinvite/{userId}', { organizationId, userId }),
    revokeInvitation: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/revoke/{userId}', { organizationId, userId }),
    resolveInvitationRevoke: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/revoke/{userId}/resolve', { organizationId, userId }),
    assignPeerSupportLead: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/users/{userId}/assign-peer-support-lead', {
        organizationId,
        userId,
      }),
    revokePeerSupportLead: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/users/{userId}/revoke-peer-support-lead', {
        organizationId,
        userId,
      }),
    upgradeToPeerSupport: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/users/{userId}/upgrade-to-peer-support', {
        organizationId,
        userId,
      }),
    downgradeToEmployee: (organizationId: string, userId: string) =>
      withParams('/api/v1/organization/{organizationId}/users/{userId}/downgrade-to-employee', {
        organizationId,
        userId,
      }),
  },

  organizationLeads: {
    root: stripApiPrefix('/api/v1/admin/temp-lead-orgs'),
    bySlug: (slug: string) => withId('/api/v1/admin/temp-lead-orgs/{slug}', { id: slug, paramName: 'slug' }),
  },

  providers: {
    root: stripApiPrefix('/api/v1/therapists'),
    inactive: stripApiPrefix('/api/v1/therapists/inactive'),
    matching: stripApiPrefix('/api/v1/therapists/matching'),
    byId: (id: string) => withId('/api/v1/therapists/{id}', { id, paramName: 'id' }),
    activate: (id: string) => withId('/api/v1/therapists/{id}/activate', { id, paramName: 'id' }),
    deactivate: (id: string) => withId('/api/v1/therapists/{id}/deactivate', { id, paramName: 'id' }),
    locations: (id: string) => withId('/api/v1/therapists/{id}/locations', { id, paramName: 'id' }),
    addLocation: (id: string) => withId('/api/v1/therapists/{id}/locations/add', { id, paramName: 'id' }),
    removeLocation: (id: string) => withId('/api/v1/therapists/{id}/locations/remove', { id, paramName: 'id' }),
  },

  creatorApplications: {
    root: stripApiPrefix('/api/v1/admin/content-creator-applications'),
    byId: (id: string) => withId('/api/v1/admin/content-creator-applications/{id}', { id, paramName: 'id' }),
    approve: (id: string) => withId('/api/v1/admin/content-creator-applications/{id}/approve', { id, paramName: 'id' }),
    reject: (id: string) => withId('/api/v1/admin/content-creator-applications/{id}/reject', { id, paramName: 'id' }),
  },

  content: {
    // Backoffice moderation surface (super admin only).
    adminRoot: stripApiPrefix('/api/v1/admin/content'),
    adminMyOmnia: stripApiPrefix('/api/v1/admin/content/myomnia'),
    adminById: (id: string) => withId('/api/v1/admin/content/{id}', { id, paramName: 'id' }),
    adminAuditLog: (id: string) => withId('/api/v1/admin/content/{id}/audit-log', { id, paramName: 'id' }),
    adminRemove: (id: string) => withId('/api/v1/admin/content/{id}/remove', { id, paramName: 'id' }),
    adminRestore: (id: string) => withId('/api/v1/admin/content/{id}/restore', { id, paramName: 'id' }),

    // Regular content lifecycle endpoints (super admins may mutate any content).
    byId: (id: string) => withId('/api/v1/content/{id}', { id, paramName: 'id' }),
    publish: (id: string) => withId('/api/v1/content/{id}/publish', { id, paramName: 'id' }),
    unpublish: (id: string) => withId('/api/v1/content/{id}/unpublish', { id, paramName: 'id' }),
    thumbnail: (id: string) => withId('/api/v1/content/{id}/thumbnail', { id, paramName: 'id' }),
    articleImage: (id: string) => withId('/api/v1/content/{id}/article-image', { id, paramName: 'id' }),
    videoMuxUploadUrl: (id: string) => withId('/api/v1/content/{id}/video/mux/upload-url', { id, paramName: 'id' }),
    videoYoutube: (id: string) => withId('/api/v1/content/{id}/video/youtube', { id, paramName: 'id' }),
  },

  timeline: {
    self: stripApiPrefix('/api/v1/timeline/self'),
    forUser: (id: string) => withId('/api/v1/timeline/user/{id}', { id, paramName: 'id' }),
  },

  user: {
    root: stripApiPrefix('/api/v1/user'),
    active: stripApiPrefix('/api/v1/user/active'),
    deactivated: stripApiPrefix('/api/v1/user/deactivated'),
    signIn: stripApiPrefix('/api/v1/user/login'),
    signOut: stripApiPrefix('/api/v1/user/logout'),
    forgotPassword: stripApiPrefix('/api/v1/user/forgot-password'),
    setInitialPassword: stripApiPrefix('/api/v1/user/set-password'),
    resetPassword: stripApiPrefix('/api/v1/user/reset-password'),
    verifyAuthToken: stripApiPrefix('/api/v1/user/verify-token'),
    notificationPreferences: stripApiPrefix('/api/v1/user/notification-preferences'),
    byId: (userId: string) => withId('/api/v1/user/{userId}', { id: userId, paramName: 'userId' }),
    promote: (userId: string) => withId('/api/v1/user/{userId}/promote', { id: userId, paramName: 'userId' }),
    reassign: (userId: string) => withId('/api/v1/user/{userId}/reassign', { id: userId, paramName: 'userId' }),
    reactivateOrgUser: (userId: string) =>
      withId('/api/v1/organization/users/{userId}/reactivate', { id: userId, paramName: 'userId' }),
  },
} as const;

export { apiPaths, stripApiPrefix, withId, withParams };
export type { OpenApiPath };
